import {
  type BaseField,
  Button,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  withConditional,
} from "@bleu.builders/ui";
import React, { useState } from "react";

interface ListFieldProps extends BaseField {}

const parseList = (value: unknown): string[] => {
  if (typeof value !== "string" || value === "") return [""];
  return value.split(",");
};

const serializeList = (items: string[]): string =>
  items
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .join(",");

export const ListField = withConditional<ListFieldProps>(({ form, field }) => {
  return (
    <FormField
      control={form.control}
      name={field.name}
      rules={{
        validate: (value: unknown) => {
          if (!field.required) return true;
          return serializeList(parseList(value)).length > 0 || "Required";
        },
      }}
      render={({ field: formField }) => (
        <ListFieldInner
          formField={formField}
          placeholder={field.placeholder}
        />
      )}
    />
  );
});

interface ListFieldInnerProps {
  // biome-ignore lint/suspicious/noExplicitAny: RHF field shape
  formField: any;
  placeholder?: string;
}

function ListFieldInner({ formField, placeholder }: ListFieldInnerProps) {
  const [items, setItems] = useState<string[]>(() => parseList(formField.value));

  const update = (next: string[]) => {
    setItems(next);
    formField.onChange(serializeList(next));
  };

  return (
    <FormItem className="flex flex-col items-start gap-2 w-full">
      <div className="flex flex-col gap-2 w-full">
        {items.map((item, index) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: stable on append-only
            key={index}
            className="flex gap-2 items-center w-full"
          >
            <FormControl className="w-full">
              <Input
                value={item}
                placeholder={placeholder}
                onChange={(e) => {
                  const next = [...items];
                  next[index] = e.target.value;
                  update(next);
                }}
              />
            </FormControl>
            {items.length > 1 && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => update(items.filter((_, i) => i !== index))}
              >
                ×
              </Button>
            )}
          </div>
        ))}
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => update([...items, ""])}
      >
        + Add
      </Button>
      <FormMessage />
    </FormItem>
  );
}
